"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import React from "react";
import { CircleCheckIcon, CircleHelpIcon, CircleIcon, Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  navigationMenuTriggerStyle,
} from "@/components/ui/navigation-menu";
import { cn } from "@/lib/utils";

import { services, industries, useCases } from "@/lib/data";
import Image from "next/image";

export function Header() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
  const pathname = usePathname();

  const isLinkActive = (href: string) => {
    if (href === "/") return pathname === href;
    return pathname.startsWith(href);
  };

  const renderNavLinks = (isMobile = false) => {
    return (
      <div>
        <NavigationMenu viewport={false}>
          <NavigationMenuList>
            <NavigationMenuItem>
              <NavigationMenuLink asChild>
                <Link
                  href="/"
                  className={cn(
                    navigationMenuTriggerStyle(),
                    isLinkActive("/") && "bg-accent/50"
                  )}
                >
                  Home
                </Link>
              </NavigationMenuLink>
            </NavigationMenuItem>

            <NavigationMenuItem>
              <NavigationMenuLink asChild>
                <Link
                  href="/about"
                  className={cn(
                    navigationMenuTriggerStyle(),
                    isLinkActive("/about") && "bg-accent/50"
                  )}
                >
                  About Us
                </Link>
              </NavigationMenuLink>
            </NavigationMenuItem>

            <NavigationMenuItem>
              <NavigationMenuTrigger className={cn(
                navigationMenuTriggerStyle(),
                isLinkActive("/services") && "bg-accent/50"
              )} >
                <NavigationMenuLink asChild>
                  <Link
                    href="/services"
                  >
                    Services
                  </Link>
                </NavigationMenuLink>
              </NavigationMenuTrigger>
              <NavigationMenuContent>
                <ul className="grid w-[200px] gap-4">
                  {services.map((service) => (
                    <li key={service.slug}>
                      <NavigationMenuLink asChild>
                        <Link
                          href={`/services/${service.slug}`}
                          className="block select-none space-y-1 p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                        >
                          <div className="flex items-center gap-2">
                            <service.icon className="h-4 w-4" />
                            <div className="text-sm font-medium leading-none">{service.title}</div>
                          </div>
                        </Link>
                      </NavigationMenuLink>
                    </li>
                  ))}
                </ul>
              </NavigationMenuContent>
            </NavigationMenuItem>


            <NavigationMenuItem>
              <NavigationMenuTrigger
                className={cn(
                  isLinkActive("/industries") && "bg-accent/50"
                )}
              >
                <NavigationMenuLink asChild>
                  <Link
                    href="/industries"
                  >
                    Industries
                  </Link>
                </NavigationMenuLink>
              </NavigationMenuTrigger>
              <NavigationMenuContent>
                <ul className="grid w-[200px] gap-4">
                  {industries.map((industry) => (
                    <li key={industry.slug}>
                      <NavigationMenuLink asChild>
                        <Link
                          href={`/industries/${industry.slug}`}
                          className="block select-none space-y-1 p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                        >
                          <div className="flex items-center gap-2">
                            <industry.icon className="h-4 w-4" />
                            <div className="text-sm font-medium leading-none">{industry.title}</div>
                          </div>
                        </Link>
                      </NavigationMenuLink>
                    </li>
                  ))}
                </ul>
              </NavigationMenuContent>
            </NavigationMenuItem>


            <NavigationMenuItem>
              <NavigationMenuTrigger
                className={cn(
                  isLinkActive("/use-cases") && "bg-accent/50"
                )}>
                <NavigationMenuLink asChild>
                  <Link
                    href="/use-cases"
                  >
                    Use Cases
                  </Link>
                </NavigationMenuLink>
              </NavigationMenuTrigger>
              <NavigationMenuContent>
                <ul className="grid w-[200px] gap-4">
                  {useCases.map((useCase) => (
                    <li key={useCase.slug}>
                      <NavigationMenuLink asChild>
                        <Link
                          href={`/use-cases/${useCase.slug}`}
                          className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                        >
                          <div className="flex items-center gap-2">
                            <useCase.icon className="h-4 w-4" />
                            <div className="text-sm font-medium leading-none">{useCase.title}</div>
                          </div>

                        </Link>
                      </NavigationMenuLink>
                    </li>
                  ))}
                </ul>
              </NavigationMenuContent>
            </NavigationMenuItem>

            <NavigationMenuItem>
              <NavigationMenuLink asChild>
                <Link
                  href="/contact"
                  className={cn(
                    navigationMenuTriggerStyle(),
                    isLinkActive("/contact") && "bg-accent/50"
                  )}
                >
                  Contact
                </Link>
              </NavigationMenuLink>
            </NavigationMenuItem>
          </NavigationMenuList>
        </NavigationMenu>
        <div
          className={cn(
            "pt-4 mt-4 border-t border-border",
            isMobile ? "block" : "hidden"
          )}
        >
          <Button asChild>
            <Link href="/contact?demo=true">Book a Demo</Link>
          </Button>
        </div>
      </div>
    );
  }

  function ListItem({
    title,
    children,
    href,
    ...props
  }: React.ComponentPropsWithoutRef<"li"> & { href: string }) {
    return (
      <li {...props}>
        <NavigationMenuLink asChild>
          <Link href={href}>
            <div className="text-sm leading-none font-medium">{title}</div>
            <p className="text-muted-foreground line-clamp-2 text-sm leading-snug">
              {children}
            </p>
          </Link>
        </NavigationMenuLink>
      </li>
    )
  }

  // const renderNavLinks = (isMobile = false) => (





  // <NavigationMenuItem>
  //   <NavigationMenuLink asChild>
  //     <Link
  //       href="/"
  //       className={cn(
  //         navigationMenuTriggerStyle(),
  //         isLinkActive("/") && "bg-accent/50"
  //       )}
  //     >
  //       Home
  //     </Link>
  //   </NavigationMenuLink>
  // </NavigationMenuItem>

  //         <NavigationMenuItem>
  //           <NavigationMenuLink asChild>
  //             <Link
  //               href="/about"
  //               className={cn(
  //                 navigationMenuTriggerStyle(),
  //                 isLinkActive("/about") && "bg-accent/50"
  //               )}
  //             >
  //               About Us
  //             </Link>
  //           </NavigationMenuLink>
  //         </NavigationMenuItem>

  //         <NavigationMenuItem>

  //         





  return (
    <header className="sticky top-0 z-[100] w-full border-b border-border/40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container mx-auto flex h-16 items-center justify-between px-4"><Image
        src="/tellis_logo.png"
        alt="Tellis AI"
        width={100}
        height={100}
        priority
        className="w-24 h-auto"
      />

        <div className="hidden lg:flex items-center gap-2">
          {renderNavLinks()}
          <div className="flex items-center gap-2">
            <Button asChild>
              <Link href="/contact?demo=true">Book a Demo</Link>
            </Button>
          </div>
        </div>
        <div className="lg:hidden flex items-center gap-2">
          <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon">
                <Menu className="h-6 w-6" />
                <span className="sr-only">Open menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-full max-w-sm">
              <SheetTitle className="sr-only">Mobile Navigation Menu</SheetTitle>
              <SheetDescription className="sr-only">Navigation menu for mobile devices</SheetDescription>
              <div className="flex justify-between items-center p-4 -m-4 mb-4 border-b">
                <Image src="/tellis_logo.png" alt="Tellis AI" width={100} height={100} priority className="w-24 h-auto" />
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <X className="h-6 w-6" />
                  <span className="sr-only">Close menu</span>
                </Button>
              </div>
              <nav className="flex flex-col gap-4">
                <Link href="/" className="px-4 py-2 hover:bg-accent rounded-md">Home</Link>
                <Link href="/about" className="px-4 py-2 hover:bg-accent rounded-md">About Us</Link>
                <div className="px-4 py-2">
                  <div className="font-medium mb-2">Services</div>
                  <div className="ml-4 space-y-2">
                    {services.map((service) => (
                      <Link key={service.slug} href={`/services/${service.slug}`} className="block text-sm hover:bg-accent rounded-md px-2 py-1">
                        {service.title}
                      </Link>
                    ))}
                  </div>
                </div>
                <div className="px-4 py-2">
                  <div className="font-medium mb-2">Industries</div>
                  <div className="ml-4 space-y-2">
                    {industries.map((industry) => (
                      <Link key={industry.slug} href={`/industries/${industry.slug}`} className="block text-sm hover:bg-accent rounded-md px-2 py-1">
                        {industry.title}
                      </Link>
                    ))}
                  </div>
                </div>
                <div className="px-4 py-2">
                  <div className="font-medium mb-2">Use Cases</div>
                  <div className="ml-4 space-y-2">
                    {useCases.map((useCase) => (
                      <Link key={useCase.slug} href={`/use-cases/${useCase.slug}`} className="block text-sm hover:bg-accent rounded-md px-2 py-1">
                        {useCase.title}
                      </Link>
                    ))}
                  </div>
                </div>
                <Link href="/blog" className="px-4 py-2 hover:bg-accent rounded-md">Blog</Link>
                <Link href="/contact" className="px-4 py-2 hover:bg-accent rounded-md">Contact</Link>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
